import { Link } from "@tanstack/react-router";
import { FileText, Settings } from "lucide-react";
import type { DeskRow, DeskState } from "@/api/types";
import { DESK_STATE_META } from "@/api/types";
import { UserMenu } from "@/components/user-menu";

interface DeskTopBarProps {
  desk: DeskRow | null;
  state: DeskState;
  spend: number;
  waking: boolean;
  relayReachable: boolean | null;
  onWake: () => void;
  wakeFailed: boolean;
  /**
   * True once the desk has been in a non-active state long enough that we
   * feel confident showing wake-related UI. Debounced upstream so brief
   * polling blips don't flicker the Wake button.
   */
  stableInactive: boolean;
  /** Mobile-only: open the files drawer. */
  onOpenFiles?: () => void;
  /** Mobile-only: open the settings drawer. */
  onOpenSettings?: () => void;
}

const TONE_DOT: Record<string, string> = {
  ok: "var(--ok, #6b8f5c)",
  warm: "var(--warm, #c4775a)",
  cold: "var(--t4)",
  err: "var(--err)",
  accent: "var(--accent)",
};

export function DeskTopBar({
  desk,
  state,
  spend,
  waking,
  relayReachable,
  onWake,
  wakeFailed,
  stableInactive,
  onOpenFiles,
  onOpenSettings,
}: DeskTopBarProps) {
  const meta = DESK_STATE_META[state];
  const showWake =
    !waking &&
    stableInactive &&
    (state === "stored" || state === "warm" || state === "error");
  const dotColor = TONE_DOT[meta.tone];

  // relayReachable is informative — if we're active but the relay isn't
  // responding to keepalive pings, flag it.
  const relayIssue = state === "active" && relayReachable === false;

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 border-b"
      style={{
        borderColor: "var(--blt)",
        background: "var(--surface)",
        flexShrink: 0,
      }}
    >
      <Link
        to="/"
        className="p-1 -ml-1 rounded hover:bg-[var(--hover)] transition-colors"
        style={{ color: "var(--t3)" }}
        title="Back to desks"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </Link>

      <div className="flex items-center gap-2 min-w-0 flex-shrink">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{
            background: dotColor,
            animation:
              state === "active" || waking
                ? "pulse 1.8s ease-in-out infinite"
                : undefined,
          }}
        />
        <h1
          className="truncate"
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-lg)",
            fontWeight: 600,
            letterSpacing: "-0.3px",
            color: "var(--t1)",
          }}
        >
          {desk?.name ?? "Loading…"}
        </h1>
        <span
          className="text-[11px] px-1.5 py-0.5 rounded shrink-0"
          style={{
            fontFamily: "var(--font-mono)",
            color: dotColor,
            background: `color-mix(in srgb, ${dotColor} 10%, transparent)`,
            letterSpacing: "0.3px",
          }}
        >
          {waking ? "waking…" : meta.label}
        </span>
      </div>

      {/* Middle spacer */}
      <div className="flex-1" />

      {/* Right cluster */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {relayIssue && (
          <span
            className="text-[11px] px-1.5 py-0.5 rounded flex items-center gap-1"
            style={{
              fontFamily: "var(--font-mono)",
              color: "var(--err)",
              background: "rgba(181, 64, 64, 0.08)",
            }}
            title="Keepalive to relay is failing — sprite may have slept"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <circle cx="12" cy="16" r="0.5" fill="currentColor" />
            </svg>
            <span className="hidden sm:inline">relay unreachable</span>
          </span>
        )}

        {spend > 0 && (
          <span
            className="text-[12px] tabular-nums hidden sm:inline"
            style={{ fontFamily: "var(--font-mono)", color: "var(--t3)" }}
            title="Total spend on this desk"
          >
            ${spend.toFixed(2)}
          </span>
        )}

        {showWake && !wakeFailed && (
          <button
            type="button"
            onClick={onWake}
            disabled={waking}
            className="flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded transition-colors disabled:opacity-50"
            style={{
              background: "color-mix(in srgb, var(--accent) 10%, transparent)",
              color: "var(--accent)",
              border:
                "1px solid color-mix(in srgb, var(--accent) 20%, transparent)",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
            Wake
          </button>
        )}

        {wakeFailed && (
          <button
            type="button"
            onClick={onWake}
            disabled={waking}
            className="flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1 rounded transition-colors"
            style={{
              background: "rgba(181, 64, 64, 0.08)",
              color: "var(--err)",
              border: "1px solid rgba(181, 64, 64, 0.2)",
            }}
          >
            Wake failed — retry
          </button>
        )}

        {waking && (
          <span
            className="flex items-center gap-1.5 text-[12px]"
            style={{ color: "var(--accent)" }}
          >
            <span
              className="w-3 h-3 rounded-full"
              style={{
                border: "1.5px solid var(--accent)",
                borderTopColor: "transparent",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <span className="hidden sm:inline">Waking…</span>
          </span>
        )}

        {/* Mobile-only rail replacement: Files + Settings shortcuts. On
            desktop these live in the side rail so we hide them here. */}
        {onOpenFiles && (
          <button
            type="button"
            onClick={onOpenFiles}
            className="md:hidden p-1.5 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Files"
          >
            <FileText size={15} />
          </button>
        )}
        {onOpenSettings && (
          <button
            type="button"
            onClick={onOpenSettings}
            className="md:hidden p-1.5 rounded hover:bg-[var(--hover)]"
            style={{ color: "var(--t3)" }}
            title="Settings"
          >
            <Settings size={15} />
          </button>
        )}

        <UserMenu compact />
      </div>
    </div>
  );
}
